import React, { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import api from "../../services/axiosInstance";
import ReportPreviewModal from "./ReportPreviewModal";
import { buildGRNPDF, downloadGRNPDF } from "../../utils/grnPdfUtils";

/**
 * Goods Received Note preview / print modal.
 * Fetches GRN details, generates a branded PDF via jsPDF, then shows it in the
 * shared ReportPreviewModal with preview + download actions.
 *
 * Usage:
 *   <GRNPreviewModal grnId={id} visible={visible} onCancel={...} />
 */
const GRNPreviewModal = ({ grnId, visible, onCancel }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfDataUri, setPdfDataUri] = useState(null);

  const fetchGRNData = useCallback(async () => {
    setLoading(true);
    setPdfDataUri(null);
    try {
      const res = await api.get(`/inventory/grn/get.php?id=${grnId}`);
      if (res.data && res.data.status === "success") {
        setData(res.data);
        try {
          const doc = await buildGRNPDF(res.data);
          setPdfDataUri(doc.output("bloburl"));
        } catch (pdfError) {
          console.error("PDF generation failed:", pdfError);
          message.error("Failed to generate PDF preview.");
        }
      } else {
        message.error("Failed to load Goods Received Note details.");
      }
    } catch (error) {
      message.error("Error loading Goods Received Note.");
    } finally {
      setLoading(false);
    }
  }, [grnId]);

  useEffect(() => {
    if (visible && grnId) {
      fetchGRNData();
    } else if (!visible) {
      setData(null);
      setPdfDataUri(null);
    }
  }, [visible, grnId, fetchGRNData]);

  const handleDownload = async () => {
    try {
      if (!data) return;
      await downloadGRNPDF(data);
      message.success("PDF downloaded successfully.");
    } catch (error) {
      console.error("PDF download failed:", error);
      message.error("Failed to download PDF.");
    }
  };

  return (
    <ReportPreviewModal
      visible={visible}
      onCancel={onCancel}
      title={`Goods Received Note - ${data?.master?.grn_ref || ""}`}
      pdfDataUri={pdfDataUri}
      loading={loading}
      showFiltersOption={false}
      onDownload={handleDownload}
    />
  );
};

export default GRNPreviewModal;
